/**
 * purchasesService — thin wrapper around @revenuecat/purchases-capacitor.
 *
 * All IAP calls go through this service so the rest of the app stays
 * decoupled from the RC SDK. On web/non-native the service is a no-op.
 */

import { isNativePlatform, RC_API_KEY_APPLE, RC_ENTITLEMENTS, type PlanInfo } from './revenuecat';

// Lazy-import the RC plugin only on native to avoid web-build issues
let Purchases: any = null;

async function getPlugin() {
  if (!isNativePlatform()) return null;
  if (!Purchases) {
    try {
      const mod = await import('@revenuecat/purchases-capacitor');
      Purchases = mod.Purchases;
    } catch {
      console.warn('[RC] @revenuecat/purchases-capacitor not available');
    }
  }
  return Purchases;
}

// ── Initialise ────────────────────────────────────────────────────────────────

/**
 * Call once at app startup (after user is identified).
 * Safe to call on web — will no-op.
 */
export async function initRevenueCat(userId?: string): Promise<void> {
  const P = await getPlugin();
  if (!P) return;

  try {
    await P.configure({
      apiKey: RC_API_KEY_APPLE,
      appUserID: userId || null, // null → anonymous
    });
    console.log('[RC] Configured', userId ? `for ${userId}` : '(anonymous)');
  } catch (err) {
    console.error('[RC] configure error:', err);
  }
}

/**
 * Call when the user logs in to link their RC identity to their account.
 */
export async function loginRevenueCat(userId: string): Promise<void> {
  const P = await getPlugin();
  if (!P) return;
  try {
    await P.logIn({ appUserID: userId });
    console.log('[RC] Logged in:', userId);
  } catch (err) {
    console.error('[RC] logIn error:', err);
  }
}

/**
 * Call on sign-out to reset RC to anonymous.
 */
export async function logoutRevenueCat(): Promise<void> {
  const P = await getPlugin();
  if (!P) return;
  try {
    await P.logOut();
    console.log('[RC] Logged out');
  } catch (err) {
    console.error('[RC] logOut error:', err);
  }
}

// ── Customer info & entitlements ──────────────────────────────────────────────

/**
 * Fetch the current customer's active entitlements and return the resolved
 * PlanInfo. Returns free plan if not native or no active entitlements.
 */
export async function getCurrentPlan(): Promise<PlanInfo> {
  const P = await getPlugin();
  if (!P) return { tier: 'free', isActive: false };

  try {
    const { customerInfo } = await P.getCustomerInfo();
    const entitlements = customerInfo?.entitlements?.active ?? {};

    // Enterprise takes priority over Pro
    if (entitlements[RC_ENTITLEMENTS.ENTERPRISE]) {
      const ent = entitlements[RC_ENTITLEMENTS.ENTERPRISE];
      return {
        tier: 'enterprise',
        isActive: true,
        expiresAt: ent.expirationDate ? new Date(ent.expirationDate) : undefined,
        willRenew: ent.willRenew,
        productIdentifier: ent.productIdentifier,
      };
    }

    if (entitlements[RC_ENTITLEMENTS.PRO]) {
      const ent = entitlements[RC_ENTITLEMENTS.PRO];
      return {
        tier: 'pro',
        isActive: true,
        expiresAt: ent.expirationDate ? new Date(ent.expirationDate) : undefined,
        willRenew: ent.willRenew,
        productIdentifier: ent.productIdentifier,
      };
    }

    return { tier: 'free', isActive: false };
  } catch (err) {
    console.error('[RC] getCustomerInfo error:', err);
    return { tier: 'free', isActive: false };
  }
}

// ── Offerings & packages ──────────────────────────────────────────────────────

export interface RCPackage {
  identifier: string;
  packageType: string;
  product: {
    identifier: string;
    title: string;
    description: string;
    priceString: string;
    price: number;
    currencyCode: string;
    introPrice?: {
      priceString: string;
      period: string;
      cycles: number;
    } | null;
  };
}

export interface RCOffering {
  identifier: string;
  serverDescription: string;
  availablePackages: RCPackage[];
  monthly?: RCPackage | null;
  annual?: RCPackage | null;
}

/**
 * Fetch current offerings from RevenueCat.
 * Returns null on web or on error.
 */
export async function getOfferings(): Promise<RCOffering | null> {
  const P = await getPlugin();
  if (!P) return null;

  try {
    const { offerings } = await P.getOfferings();
    return offerings?.current ?? null;
  } catch (err) {
    console.error('[RC] getOfferings error:', err);
    return null;
  }
}

// ── Purchases ─────────────────────────────────────────────────────────────────

export interface PurchaseResult {
  success: boolean;
  plan?: PlanInfo;
  error?: string;
  userCancelled?: boolean;
}

/**
 * Purchase a specific package.
 */
export async function purchasePackage(pkg: RCPackage): Promise<PurchaseResult> {
  const P = await getPlugin();
  if (!P) return { success: false, error: 'IAP not available on this platform' };

  try {
    const { customerInfo: _pc } = await P.purchasePackage({ aPackage: pkg });
    const plan = await getCurrentPlan();
    return { success: true, plan };
  } catch (err: any) {
    if (err?.userCancelled || err?.code === 'PURCHASE_CANCELLED') {
      return { success: false, userCancelled: true };
    }
    console.error('[RC] purchasePackage error:', err);
    return { success: false, error: err?.message || 'Purchase failed' };
  }
}

/**
 * Restore previous purchases (required by App Store guidelines).
 */
export async function restorePurchases(): Promise<PurchaseResult> {
  const P = await getPlugin();
  if (!P) return { success: false, error: 'IAP not available on this platform' };

  try {
    const { customerInfo: _rc } = await P.restorePurchases();
    const plan = await getCurrentPlan();
    return { success: true, plan };
  } catch (err: any) {
    console.error('[RC] restorePurchases error:', err);
    return { success: false, error: err?.message || 'Restore failed' };
  }
}
